<template>
  <v-card class="mb-4" variant="outlined">
    <v-card-title class="text-h6 d-flex align-center">
      <v-icon class="mr-2" color="primary">mdi-strategy</v-icon>
      Estrategia de Picking
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6">
          <v-radio-group 
            v-model="strategy.level" 
            @update:model-value="onLevelChange"
            class="mt-0"
          >
            <v-radio 
              label="Por Orden/Tarea (aplica a todos los productos)" 
              value="order"
              color="primary"
            ></v-radio>
            <v-radio 
              label="Por Producto (seleccionar individualmente)" 
              value="product"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="6" v-if="strategy.level === 'order'">
          <v-select
            v-model="strategy.strategy"
            :items="strategyOptions"
            item-title="text"
            item-value="value"
            label="Estrategia para toda la orden"
            @update:model-value="onStrategyChange"
            clearable
            variant="outlined"
            density="compact"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
      
      <!-- Explicación de la estrategia seleccionada -->
      <v-alert 
        v-if="strategy.strategy && strategy.level === 'order'" 
        type="info" 
        variant="tonal" 
        class="mt-3"
        :icon="getStrategyIcon()"
      >
        <div class="font-weight-medium">{{ getStrategyTitle() }}</div>
        <div class="text-body-2 mt-1">{{ getStrategyDescription() }}</div>
      </v-alert>
      
      <!-- Alerta para selección por producto -->
      <v-alert 
        v-if="strategy.level === 'product'" 
        type="warning" 
        variant="tonal" 
        class="mt-3"
        icon="mdi-account-cog"
      >
        <div class="font-weight-medium">Selección Individual</div>
        <div class="text-body-2 mt-1">
          Deberás seleccionar una estrategia para cada producto individualmente. 
          Esto te dará máximo control pero requerirá más configuración.
        </div>
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'PickingStrategySelector',
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        level: 'order',
        strategy: null
      })
    }
  },
  emits: ['update:modelValue', 'strategy-changed'],
  
  data() {
    return {
      strategy: { ...this.modelValue },
      
      strategyOptions: [
        {
          value: 'fifo',
          text: 'FIFO - First In, First Out',
          description: 'Primero en entrar, primero en salir. Usa los productos más antiguos primero.',
          icon: 'mdi-arrow-right'
        },
        {
          value: 'lifo',
          text: 'LIFO - Last In, First Out',
          description: 'Último en entrar, primero en salir. Usa los productos más recientes primero.',
          icon: 'mdi-arrow-left'
        },
        {
          value: 'fefo',
          text: 'FEFO - First Expired, First Out',
          description: 'Primero en vencer, primero en salir. Usa productos con fecha de vencimiento más cercana.',
          icon: 'mdi-calendar-clock'
        },
        {
          value: 'manual',
          text: 'Selección Manual',
          description: 'Permite seleccionar manualmente lote, S/N o fecha de vencimiento específicos.',
          icon: 'mdi-hand-pointing-right'
        }
      ]
    }
  },
  
  watch: {
    modelValue: {
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.strategy)) {
          this.strategy = { ...newValue }
        }
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    onLevelChange(newLevel) {
      // Si cambia a 'order', limpiar estrategias individuales de productos
      if (newLevel === 'order') {
        this.strategy.strategy = null
      }
      
      // Emitir cambio de modelo
      this.$emit('update:modelValue', { ...this.strategy })
      
      this.$emit('strategy-changed', {
        type: 'level-changed',
        level: newLevel,
        strategy: this.strategy.strategy
      })
    },
    
    onStrategyChange(newStrategy) {
      this.strategy.strategy = newStrategy
      
      // Emitir cambio de modelo
      this.$emit('update:modelValue', { ...this.strategy })
      
      this.$emit('strategy-changed', {
        type: 'strategy-changed',
        level: this.strategy.level,
        strategy: newStrategy
      })
    },
    
    getStrategyTitle() {
      const option = this.strategyOptions.find(opt => opt.value === this.strategy.strategy)
      return option ? option.text : ''
    },
    
    getStrategyDescription() {
      const option = this.strategyOptions.find(opt => opt.value === this.strategy.strategy)
      return option ? option.description : ''
    },
    
    getStrategyIcon() {
      const option = this.strategyOptions.find(opt => opt.value === this.strategy.strategy)
      return option ? option.icon : 'mdi-information'
    }
  }
}
</script>

<style scoped>
.v-card-title {
  padding-bottom: 8px;
}

.v-radio-group {
  margin-top: 0 !important;
}

.v-alert {
  border-left: 4px solid;
}
</style> 